<template>
  <DsPageContent v-if="content">
    <PaymentMethodPageHeader
      :header-content="{
        title: content.paymentMethod.name,
        description: content.paymentMethod.description,
        link: { text: filterLabels.morePaymentMethods, url: slugParent }
      }"
      :header-links="{ pricingLink: content.pricingLink, documentationLink: content.documentationLink }"
      :image="{
        alt: paymentMethodImage.name,
        src: paymentMethodImage.src
      }"
      :background-color="paymentMethodImage.color"
      :pricing="{
        pricingData: content.paymentMethod.pricingData,
        countryData: content.paymentMethod.countryData,
        submethods: content.paymentMethod.submethods
      }"
    />

    <section
      v-if="content.usps.length"
      class="ds-margin-top-48 ds-md-margin-top-96 ds-margin-bottom-0 ds-md-margin-bottom-48"
    >
      <div class="ds-container">
        <div class="ds-grid">
          <DsCardTextIcon
            v-for="(usp, index) in content.usps"
            :key="`usp-${index}`"
            :icon="{ name: usp.icon.name, category: usp.icon.category }"
            :title="usp.title"
            class="ds-grid__col ds-grid__col--12 ds-md-grid__col--6 ds-lg-grid__col--3 ds-margin-bottom-24"
          >
            <RichText
              v-if="usp.description"
              :data="usp.description"
            />
          </DsCardTextIcon>
        </div>
      </div>
    </section>

    <div
      v-if="onPageNavItems && onPageNavItems.length"
      class="sticky-top ds-width-full"
    >
      <DsOnPageNavigation
        disposition="horizontal"
        :items="onPageNavItems"
        :screen="screen"
        title="on this page"
        :show-numbers="false"
        :update-route="true"
        :initial-active-item="route.hash"
        portal-target="header"
        use-grid
      />
    </div>

    <section
      v-if="pageContent?.content?.paymentMethod.submethods.length"
      class="ds-padding-top-48 ds-md-padding-top-96 ds-background-color-grey-100"
      data-id="submethods"
    >
      <div class="ds-container">
        <PaymentMethodSubMethods
          :title="pageContent.content.paymentMethod.submethodsTitle || filterLabels.submethodsSectionTitle"
          :submethods="pageContent.content.paymentMethod.submethods"
          class="ds-padding-bottom-48 ds-md-padding-bottom-96"
        />
        <div
          v-if="pageContent.content?.howItWorks.length"
          class="divider"
        />
      </div>
    </section>

    <section
      v-if="pageContent.content?.howItWorks.length"
      class="ds-padding-y-48 ds-md-padding-y-96 ds-background-color-grey-100 ds-display-flex ds-flex-direction-column"
      data-id="howItWorks"
    >
      <div class="ds-container">
        <div class="ds-grid">
          <div class="ds-grid__col">
            <h2 class="ds-heading ds-margin-bottom-24">
              {{ filterLabels.howItWorksSectionTitle }}
            </h2>
          </div>
        </div>
      </div>
      <!-- More than one Image Gallery render in tabs -->
      <DsTabs
        v-if="pageContent.content.howItWorks.length > 1"
        :screen="screen"
        :label="filterLabels.howItWorksSectionTitle"
        type="buttons"
        contained
        use-grid
        full-screen-content
      >
        <DsTabItem
          v-for="gallery in galleryItems"
          :title="gallery?.title"
        >
          <DsImageGallery
            :items="gallery?.items.length"
            :screen="screen"
            :width="gallery.width"
            class="ds-margin-top-24 ds-md-margin-top-0"
          >
            <template #default="{ index }">
              <div class="gallery-item">
                <img
                  :src="gallery?.items[index - 1]?.image?.original?.src"
                  :alt="gallery?.items[index - 1]?.image?.original?.name"
                />
              </div>
              <div
                v-if="gallery?.items[index - 1]?.title"
                class="ds-heading-xsmall ds-padding-top-24"
              >
                {{ gallery.items[index - 1]?.title }}
              </div>
              <div
                v-if="gallery?.items[index - 1]?.description"
                class="ds-text ds-padding-top-24"
              >
                {{ gallery?.items[index - 1]?.description }}
              </div>
            </template>
          </DsImageGallery>
        </DsTabItem>
      </DsTabs>

      <!-- one Image Gallery -->
      <DsImageGallery
        v-else-if="pageContent.content.howItWorks.length"
        :items="pageContent.content.howItWorks[0]?.items.length"
        :screen="screen"
        :width="pageContent.content.howItWorks[0]?.width"
      >
        <template #default="{ index }">
          <div class="gallery-item">
            <img
              :src="pageContent.content.howItWorks[0].items[index - 1]?.image?.original?.src"
              :alt="pageContent.content.howItWorks[0].items[index - 1]?.image?.original?.name"
            />
          </div>
          <div
            v-if="pageContent.content.howItWorks[0].items[index - 1]?.title"
            class="ds-heading-xsmall ds-padding-top-24"
          >
            {{ pageContent.content.howItWorks[0].items[index - 1]?.title }}
          </div>
          <div
            v-if="pageContent.content.howItWorks[0].items[index - 1]?.description"
            class="ds-text ds-padding-top-24"
          >
            {{ pageContent.content.howItWorks[0].items[index - 1]?.description }}
          </div>
        </template>
      </DsImageGallery>
    </section>

    <section
      v-if="content?.paymentMethod"
      class="ds-padding-top-48 ds-md-padding-top-96"
      data-id="features"
    >
      <div class="ds-container">
        <div class="ds-grid ds-justify-content-center">
          <div class="ds-grid__col ds-grid__col--12 ds-md-grid__col--8 ds-md-grid__col--no-gutter">
            <PaymentMethodFeatures
              :payment-method="content.paymentMethod"
              :title="filterLabels.featuresSectionTitle"
            />
          </div>
        </div>
      </div>
    </section>

    <section
      class="ds-margin-top-48 ds-md-margin-top-96 ds-padding-y-48 ds-md-padding-y-96 ds-background-color-grey-100"
      data-id="integrate"
    >
      <div class="ds-container">
        <div class="ds-grid ds-justify-content-center">
          <div class="ds-grid__col ds-grid__col--12 ds-md-grid__col--8 wds-grid__col--no-gutter">
            <PaymentMethodIntegrate />
          </div>
        </div>
      </div>
    </section>
    <!-- Page: Faq section -->
    <DsSection
      v-if="pageContent?.content?.faq"
      class="ds-padding-bottom-48 ds-md-padding-bottom-96 ds-background-color-grey-100"
      data-id="faq"
      :with-space="false"
    >
      <div class="ds-grid ds-justify-content-center">
        <div class="ds-grid__col ds-grid__col--12 ds-md-grid__col--8 ds-md-grid__col--no-gutter">
          <div class="divider ds-margin-bottom-24 ds-md-margin-bottom-96" />
          <FaqGroup v-bind="pageContent.content.faq" />
        </div>
      </div>
    </DsSection>
    <!--   Page: Related Payment Methods-->
    <section
      :aria-hidden="!relatedContent || !relatedContent.length"
      class="ds-padding-top-48 ds-md-padding-top-96"
      data-id="related"
    >
      <div
        class="ds-container"
        v-if="relatedContent && relatedContent.length"
      >
        <PaymentMethodRelatedList
          :items="relatedContent"
          :loading="!relatedContent"
          :title="filterLabels.relatedPaymentMethodsSectionTitle"
        />
      </div>

      <!--&lt;!&ndash;    // Else spinner&ndash;&gt;-->
    </section>
    <!-- Page: Call to Action -->
    <DsCallToAction
      v-if="pageContent?.content?.callToAction"
      v-bind="pageContent.content.callToAction"
      :with-space-top="false"
    />
  </DsPageContent>
</template>
<script setup>
import {
  DsCallToAction,
  DsCardTextIcon,
  DsImageGallery,
  DsOnPageNavigation,
  DsPageContent,
  DsSection,
  DsTabItem,
  DsTabs
} from 'awds'
import { mapperRelatedPaymentMethodsDetail } from '../../data-layer/page-payment-methods-detail/mapper-related-payment-methods-detail'
import queryPagePaymentMethodsDetail from '../../data-layer/page-payment-methods-detail/query-page-payment-methods-detail'
import PaymentMethodSubMethods from '~/components/payment-methods-submethods/payment-methods-submethods.vue'

const { queryContentful } = useQueries()
const { dataPageMapper } = useDataMapper()
const { getSlug } = usePageUtils()
const { getLocaleIso } = useLocales()
const route = useRoute()
const { locale: localeRef } = useI18n()
const slug = getSlug(route.path)
const locale = getLocaleIso(localeRef.value)
const { screen } = useScreen()
const { getParentPath } = usePageUtils()
const store = useRoutesStore()

const { labels, onBeforeAdyenData, onAfterAdyenData, mappers } = usePaymentMethodsPageData()

await onBeforeAdyenData({ withRoutes: true, globalDataContentType: 'PagePaymentMethodsDetail' })

const { data } = await useAsyncData('payment-methods-details-' + slug + '-' + localeRef.value, async () => {
  const paymentMethodsDetailQuery = queryContentful(
    {
      query: queryPagePaymentMethodsDetail,
      variables: { slug, locale, preview: useRuntimeConfig().public.preview }
    },
    { log: false }
  )

  const [paymentsMethodsDetailData] = await Promise.all([paymentMethodsDetailQuery])

  const entry = paymentsMethodsDetailData?.page?.items[0]

  if (!entry) {
    return null
  }

  try {
    const paymentMethodDetails = await $fetch(
      '/api/v3/payment-methods_data/payment-method/' + entry.paymentMethod.sys.id + '/' + localeRef.value
    )

    // We merge the data, making sure that `entry` goes at the end to use its sysId
    const pageData = dataPageMapper(
      {
        ...entry,
        ...paymentMethodDetails,
        // SysId of the PM page
        sys: entry.sys
      },
      mappers
    )

    try {
      await handleLazyUrlsMapping(pageData)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[ERROR]: Couldn't handle lazy urls in Payment Methods Detail", e)
    }

    return {
      pageData,
      slugParent: getParentPath(route.path)
    }
  } catch {
    showError({ statusCode: 404, message: 'Page not found' })
  }
})

const pageContent = computed(() => {
  return data.value?.pageData || {}
})
const content = computed(() => {
  return data.value?.pageData?.content
})

const slugParent = computed(() => {
  return data.value?.slugParent
})

const paymentMethodImage = computed(() => {
  return pageContent.value?.content?.paymentMethod?.image?.original || {}
})

const filterLabels = computed(() => {
  return labels.value
})

const onPageNavItems = computed(() => {
  const items = [
    {
      label: filterLabels.value.submethodsSectionNavigationTitle,
      slug: 'submethods'
    },
    {
      label: filterLabels.value.howItWorksSectionTitle,
      slug: 'howItWorks'
    },
    {
      label: filterLabels.value.featuresSectionTitle,
      slug: 'features'
    },
    {
      label: filterLabels.value.integrateSectionNavigationTitle,
      slug: 'integrate'
    },
    {
      label: filterLabels.value.faqSectionNavigationTitle,
      slug: 'faq'
    },
    {
      label: filterLabels.value.relatedPaymentMethodsSectionNavigationTitle,
      slug: 'related'
    }
  ]
  // Filter out the sections that have no content
  return items.filter((item) => {
    return (
      ['features', 'integrate'].includes(item.slug) ||
      pageContent.value?.content?.paymentMethod?.[item.slug]?.length ||
      pageContent.value?.content?.[item.slug]?.length ||
      !!(Array.isArray(pageContent.value?.content?.[item.slug])
        ? pageContent.value?.content?.[item.slug]?.length
        : pageContent.value?.content?.[item.slug])
    )
  })
})

const galleryItems = computed(() => {
  return pageContent.value?.content?.howItWorks
})

onMounted(async () => {
  await nextTick()
  fetchPaymentMethodsDataForRelatedContent()
})

const allPMsData = ref(null)

const fetchPaymentMethodsDataForRelatedContent = async () => {
  try {
    const data = await $fetch('/api/v3/payment-methods_data/payment-methods' + '/' + localeRef.value)
    allPMsData.value = data[localeRef.value]?.paymentsMethodsData
  } catch (e) {
    console.error(`fetchPaymentMethodsDataForRelatedContent breaking ${e}`)
  }
}

const relatedContent = computed(() => {
  const items = content.value?.related
  if (!items || !allPMsData.value) {
    return null
  }

  const { getUrl } = useRoutes(store.routes, localeRef)
  const related = mapperRelatedPaymentMethodsDetail(items, allPMsData.value, getUrl)
  return related
})

await onAfterAdyenData(data.value)
</script>

<style scoped lang="scss">
@import 'awds/foundation-vars.scss';
@import 'awds/foundation-mixins.scss';

.gallery-item {
  background-color: $color-white;
  width: 100%;

  img {
    display: block;
    height: 100%;
    width: 100%;
  }
}
</style>
